import type {
  CreateSubscriptionCategoryInput,
  UpdateSubscriptionCategoryInput,
  CreateSubscriptionCurrencyInput,
  UpdateSubscriptionCurrencyInput,
  CreateSubscriptionStatusInput,
  UpdateSubscriptionStatusInput,
  CreateSubscriptionBillingCycleInput,
  UpdateSubscriptionBillingCycleInput,
  CreateSubscriptionInput,
  UpdateSubscriptionInput,
  RenewSubscriptionInput,
  CancelSubscriptionInput,
  ListSubscriptionsQuery,
  ListSubscriptionAuditQuery,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Domain types ─────────────────────────────────────────────────────────────

export interface SubscriptionCategory {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionCurrency {
  id: string;
  code: string;
  symbol: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
}

export interface SubscriptionStatus {
  id: string;
  name: string;
  color: string;
  is_default: boolean;
  display_order: number;
  is_active: boolean;
}

export interface SubscriptionBillingCycle {
  id: string;
  name: string;
  code: string;
  interval_months: number | null;
  interval_days: number | null;
  is_recurring: boolean;
  is_active: boolean;
}

export interface SubscriptionListItem {
  id: string;
  name: string;
  vendor_name: string | null;
  amount: number;
  start_date: string;
  end_date: string | null;
  next_due_date: string | null;
  last_renewed_at: string | null;
  renewal_count: number;
  reminder_days: number;
  auto_renewal: boolean;
  notes: string | null;
  created_at: string;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  department_id: string | null;
  department_name: string | null;
  assigned_to_id: string | null;
  assigned_first: string | null;
  assigned_last: string | null;
  billing_cycle_id: string;
  billing_cycle_name: string;
  billing_cycle_code: string;
  billing_cycle_recurring: boolean;
  currency_id: string;
  currency_code: string;
  currency_symbol: string;
  status_id: string;
  status_name: string;
  status_color: string;
  status_is_default: boolean;
}

export interface SubscriptionDetail extends SubscriptionListItem {
  custom_interval_days: number | null;
  interval_months: number | null;
  interval_days: number | null;
  is_recurring: boolean;
  currency_name: string;
  created_by_first: string | null;
  created_by_last: string | null;
}

export interface SubscriptionRenewalHistory {
  id: string;
  subscription_id: string;
  renewed_at: string;
  amount: number;
  previous_due: string | null;
  new_due_date: string | null;
  notes: string | null;
  renewed_by: string | null;
  renewed_by_first: string | null;
  renewed_by_last: string | null;
  renewal_type: 'manual' | 'auto';
  created_at: string;
}

export interface SubscriptionAuditLog {
  id: string;
  action: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
  subscription_name: string | null;
  actor_first: string | null;
  actor_last: string | null;
}

export interface SubscriptionStats {
  total_subscriptions: number;
  active_subscriptions: number;
  expiring_soon_count: number;
  monthly_cost: number;
  by_status: Array<{ name: string; color: string; cnt: number }>;
  cost_by_category: Array<{ name: string; total: number }>;
  expiring_soon: Array<{ id: string; name: string; next_due_date: string; reminder_days: number; currency_symbol: string; amount: number }>;
  upcoming_renewals: Array<{ id: string; name: string; next_due_date: string; amount: number; currency_symbol: string; billing_cycle_name: string; status_name: string; status_color: string }>;
  recent_activity: Array<SubscriptionAuditLog>;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ── Service ──────────────────────────────────────────────────────────────────

export const subscriptionService = {
  // Master — Categories
  listCategories: () => api.get<{ data: SubscriptionCategory[] }>('/subscriptions/categories').then((r) => r.data.data),
  createCategory: (input: CreateSubscriptionCategoryInput) => api.post('/subscriptions/categories', input),
  updateCategory: (id: string, input: UpdateSubscriptionCategoryInput) => api.patch(`/subscriptions/categories/${id}`, input),
  deleteCategory: (id: string) => api.delete(`/subscriptions/categories/${id}`),

  // Master — Currencies
  listCurrencies: () => api.get<{ data: SubscriptionCurrency[] }>('/subscriptions/currencies').then((r) => r.data.data),
  createCurrency: (input: CreateSubscriptionCurrencyInput) => api.post('/subscriptions/currencies', input),
  updateCurrency: (id: string, input: UpdateSubscriptionCurrencyInput) => api.patch(`/subscriptions/currencies/${id}`, input),
  deleteCurrency: (id: string) => api.delete(`/subscriptions/currencies/${id}`),

  // Master — Statuses
  listStatuses: () => api.get<{ data: SubscriptionStatus[] }>('/subscriptions/statuses').then((r) => r.data.data),
  createStatus: (input: CreateSubscriptionStatusInput) => api.post('/subscriptions/statuses', input),
  updateStatus: (id: string, input: UpdateSubscriptionStatusInput) => api.patch(`/subscriptions/statuses/${id}`, input),
  deleteStatus: (id: string) => api.delete(`/subscriptions/statuses/${id}`),

  // Master — Billing Cycles
  listBillingCycles: () => api.get<{ data: SubscriptionBillingCycle[] }>('/subscriptions/billing-cycles').then((r) => r.data.data),
  createBillingCycle: (input: CreateSubscriptionBillingCycleInput) => api.post('/subscriptions/billing-cycles', input),
  updateBillingCycle: (id: string, input: UpdateSubscriptionBillingCycleInput) => api.patch(`/subscriptions/billing-cycles/${id}`, input),
  deleteBillingCycle: (id: string) => api.delete(`/subscriptions/billing-cycles/${id}`),

  // Subscriptions CRUD
  list: (params: ListSubscriptionsQuery) =>
    api.get<{ data: SubscriptionListItem[]; pagination: Pagination }>('/subscriptions', { params }).then((r) => r.data),
  get: (id: string) => api.get<{ data: SubscriptionDetail }>(`/subscriptions/${id}`).then((r) => r.data.data),
  create: (input: CreateSubscriptionInput) => api.post<{ data: { id: string } }>('/subscriptions', input).then((r) => r.data.data),
  update: (id: string, input: UpdateSubscriptionInput) => api.patch(`/subscriptions/${id}`, input),
  delete: (id: string) => api.delete(`/subscriptions/${id}`),

  // Analytics
  getStats: () => api.get<{ data: SubscriptionStats }>('/subscriptions/stats').then((r) => r.data.data),
  getExpiring: (days = 30) => api.get<{ data: SubscriptionListItem[] }>('/subscriptions/expiring', { params: { days } }).then((r) => r.data.data),

  // Lifecycle
  renew: (id: string, input: RenewSubscriptionInput) => api.post(`/subscriptions/${id}/renew`, input),
  cancel: (id: string, input: CancelSubscriptionInput) => api.post(`/subscriptions/${id}/cancel`, input),
  pause: (id: string) => api.post(`/subscriptions/${id}/pause`),
  resume: (id: string) => api.post(`/subscriptions/${id}/resume`),

  // History & Audit
  getHistory: (id: string, page = 1, limit = 20) =>
    api.get<{ data: SubscriptionRenewalHistory[]; pagination: Pagination }>(`/subscriptions/${id}/history`, { params: { page, limit } }).then((r) => r.data),
  getAuditLogs: (params: ListSubscriptionAuditQuery) =>
    api.get<{ data: SubscriptionAuditLog[]; pagination: Pagination }>('/subscriptions/audit-logs', { params }).then((r) => r.data),

  // Export
  exportCsv: async (params: ListSubscriptionsQuery) => {
    const r = await api.get('/subscriptions/export/csv', { params, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data as BlobPart], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscriptions.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
};
